import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@radix-ui/react-select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import RichTextEditor from "../Questions/RichTextEditor";
import axios from "axios";

const api = axios.create({
  baseURL: 'https://localhost:7022',
  headers: {
    accept: "*/*",
    // Authorization: `Bearer ${token}`,
  },
});

export const JobPostForm = () => {
  const [formData, setFormData] = useState({
    jobTitle: "",
    companyName: "",
    location: "",
    description: "",
    expirationDate: "",
    contactEmail: "",
    roleType: 1, // 1: FullTime, 2: PartTime, 3: Internship
    workplaceType: 1, // 1: Onsite, 2: Remote, 3: Hybrid
    employerType: "Company",
    preferredCourses: "",
    preferredSkills: "",
    // type: "full-time", // giữ lại nếu muốn dùng
    // requirements: "", // giữ lại nếu muốn dùng
    // salaryMin: "",
    // salaryMax: "",
    // imageUrl: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Chuyển preferredIndexes, preferredCourses, preferredSkills thành array
      const jobData = {
        jobTitle: formData.jobTitle,
        companyName: formData.companyName,
        location: formData.location,
        description: formData.description,
        expirationDate: formData.expirationDate
          ? new Date(formData.expirationDate).toISOString()
          : undefined,
        contactEmail: formData.contactEmail,
        roleType: Number(formData.roleType), // 1: FullTime, 2: PartTime, 3: Internship
        workplaceType: Number(formData.workplaceType), // 1: Onsite, 2: Remote, 3: Hybrid
        employerType: formData.employerType,
        preferredCourses: Array.isArray(formData.preferredCourses)
          ? formData.preferredCourses
          : formData.preferredCourses
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean),
        preferredSkills: Array.isArray(formData.preferredSkills)
          ? formData.preferredSkills
          : formData.preferredSkills
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean),
        // type: formData.type, // giữ lại nếu muốn dùng
        // requirements: formData.requirements, // giữ lại nếu muốn dùng
        // salary: {
        //   min: parseInt(formData.salaryMin),
        //   max: parseInt(formData.salaryMax),
        // },
        // imageUrl: formData.imageUrl,
      };
      // Gọi API mới qua axios instance
      console.log("Posting job data:", jobData); // Debug log
      await api.post("/JobBoard/job-opportunity", jobData);
      toast.success("Job posted successfully!");
      setFormData({
        jobTitle: "",
        companyName: "",
        location: "",
        description: "",
        expirationDate: "",
        contactEmail: "",
        roleType: 1,
        workplaceType: 1,
        employerType: "Company",
        preferredCourses: "",
        preferredSkills: "",
        // type: "full-time",
        // requirements: "",
        // salaryMin: "",
        // salaryMax: "",
        // imageUrl: "",
      });
      // fetchJobs();
    } catch (error) {
      console.error("Error posting job:", error);
      toast.error("Failed to post job");
    }
  };

  return (
    <Card className="mb-8 border-0 shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Plus className="h-5 w-5" />
          <span>Post New Job</span>
        </CardTitle>
        <CardDescription>
          Fill in the details to create a new job posting
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                Job Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="jobTitle"
                value={formData.jobTitle}
                onChange={(e) =>
                  setFormData({ ...formData, jobTitle: e.target.value })
                }
                placeholder="e.g. Senior Software Engineer"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>
                Company Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="companyName"
                value={formData.companyName}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    companyName: e.target.value,
                  })
                }
                placeholder="e.g. Tech Corp"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                Workplace Type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={String(formData.workplaceType)}
                onValueChange={(value) =>
                  setFormData({ ...formData, workplaceType: Number(value) })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select workplace type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Onsite</SelectItem>
                  <SelectItem value="2">Remote</SelectItem>
                  <SelectItem value="3">Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Job Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    location: e.target.value,
                  })
                }
                placeholder="e.g. San Francisco, CA"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                Employer Type <span className="text-red-500">*</span>
              </Label>
              <Input
                id="employerType"
                value={formData.employerType}
                onChange={(e) =>
                  setFormData({ ...formData, employerType: e.target.value })
                }
                placeholder="e.g. Company, NGO, Freelancer, ..."
                required
              />
            </div>
            <div className="space-y-2">
              <Label>
                Role Type <span className="text-red-500">*</span>
              </Label>
              <Select
                value={String(formData.roleType)}
                onValueChange={(value) =>
                  setFormData({ ...formData, roleType: Number(value) })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Full Time</SelectItem>
                  <SelectItem value="2">Part Time</SelectItem>
                  <SelectItem value="3">Internship</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                Expiration Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="expirationDate"
                type="date"
                value={formData.expirationDate}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    expirationDate: e.target.value,
                  })
                }
                required
              />
            </div>
            <div className="space-y-2">
              <Label>
                Contact Email <span className="text-red-500">*</span>
              </Label>
              <Input
                id="contactEmail"
                type="email"
                value={formData.contactEmail}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    contactEmail: e.target.value,
                  })
                }
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                Preferred Courses (comma separated)
              </Label>
              <Input
                id="preferredCourses"
                value={formData.preferredCourses}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    preferredCourses: e.target.value,
                  })
                }
                placeholder="kotlin, java"
              />
            </div>
            <div className="space-y-2">
              <Label>
                Preferred Skills (comma separated)
              </Label>
              <Input
                id="preferredSkills"
                value={formData.preferredSkills}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    preferredSkills: e.target.value,
                  })
                }
                placeholder="programming"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Job Description</Label>
            <RichTextEditor
              value={formData.description}
              onChange={(value: any) =>
                setFormData({
                  ...formData,
                  description: value,
                })
              }
              placeholder="Describe the job role, responsibilities, and what you're looking for..."
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700"
          >
            {/* {editingJob ? "Update Job" : "Post Job"} */}
            Post Job
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
